/**
 * Shared helpers for transparent alpha theme videos.
 * Desktop: VP9 WebM with alpha. Mobile: opaque MP4 with page background baked in.
 */
(function (global) {
  'use strict';

  function needMobileMp4FromUA(ua) {
    ua = ua || (typeof navigator !== 'undefined' ? navigator.userAgent : '');
    if (/iPhone|iPad|iPod/i.test(ua)) return true;
    if (/Android/i.test(ua)) return true;
    if (
      /Safari/i.test(ua) &&
      !/Chrome|Chromium|CriOS|FxiOS|EdgiOS|Edg|OPR|Opera|SamsungBrowser|Android/i.test(ua)
    ) {
      return true;
    }
    return false;
  }

  function classifySource(source) {
    var hint = (source.type || '') + ' ' + (source.src || '');
    if (/webm/i.test(hint)) return 'webm';
    if (/\.mp4/i.test(hint) || (/\/mp4/i.test(hint) && !/hvc1/i.test(hint))) return 'mp4';
    if (/quicktime|hvc1|\.mov/i.test(hint)) return 'hevc';
    if (/mp4/i.test(hint)) return 'mp4';
    return '';
  }

  function pickAlphaSource(sources) {
    var webm = null;
    var mobile = null;
    var list = sources.length ? sources : [];

    for (var i = 0; i < list.length; i++) {
      var kind = classifySource(list[i]);
      if (kind === 'webm') webm = list[i];
      if (kind === 'mp4' || kind === 'hevc') mobile = list[i];
    }

    var pick = null;
    var format = '';

    if (list.length <= 1) {
      pick = list[0] || null;
      if (pick) format = classifySource(pick) || 'webm';
    } else if (webm && mobile) {
      var needMobile = needMobileMp4FromUA();
      pick = needMobile ? mobile : webm;
      format = needMobile ? classifySource(mobile) : 'webm';
    } else {
      pick = mobile || webm;
      format = pick ? classifySource(pick) || 'webm' : '';
    }

    return { pick: pick, format: format, webm: webm, mobile: mobile };
  }

  function isAlphaVideoVisible(video) {
    if (!video || !video.isConnected) return false;

    var node = video;
    while (node && node !== document.documentElement) {
      var style = window.getComputedStyle(node);
      if (style.display === 'none' || style.visibility === 'hidden') return false;
      node = node.parentElement;
    }

    var rect = video.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  }

  function applyIosInlineAttrs(video) {
    video.muted = true;
    video.playsInline = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
  }

  function applyPickedSource(video, pick, format, sourceCount) {
    if (!pick) return false;

    video.dataset.alphaSourcePicked = 'true';
    video.dataset.alphaFormat = format;

    if (format === 'mp4') {
      video.dataset.alphaOpaque = 'true';
    } else {
      delete video.dataset.alphaOpaque;
    }

    if (sourceCount > 1) {
      while (video.firstChild) video.removeChild(video.firstChild);
      var next = document.createElement('source');
      next.src = pick.src;
      next.type = pick.type;
      video.appendChild(next);
    }

    return true;
  }

  function preloadMobileOnce(src) {
    if (!src || document.documentElement.dataset.alphaVideoPreloaded) return;
    document.documentElement.dataset.alphaVideoPreloaded = '1';
    var preloadLink = document.createElement('link');
    preloadLink.rel = 'preload';
    preloadLink.as = 'video';
    preloadLink.href = src;
    document.head.appendChild(preloadLink);
  }

  function tryPlay(video) {
    if (!video) return;
    applyIosInlineAttrs(video);
    if (video.paused) {
      video.play().catch(function () {});
    }
  }

  function startAlphaVideo(video) {
    if (!video || video.dataset.alphaLoaded === 'true') {
      tryPlay(video);
      return;
    }

    applyIosInlineAttrs(video);
    video.preload = 'auto';
    video.load();
    video.dataset.alphaLoaded = 'true';

    video.addEventListener(
      'canplay',
      function () {
        tryPlay(video);
      },
      { once: true, passive: true }
    );
    tryPlay(video);
  }

  function initFromInline(video) {
    if (!video || video.tagName !== 'VIDEO' || video.dataset.alphaSourcePicked === 'true') return;

    var priority = video.dataset.alphaPriority || 'defer';
    var sources = video.querySelectorAll('source');
    var picked = pickAlphaSource(sources);

    if (
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(max-width: 61.8125rem)').matches &&
      picked.mobile
    ) {
      picked.pick = picked.mobile;
      picked.format = classifySource(picked.mobile) || 'mp4';
    }

    if (!picked.pick || !applyPickedSource(video, picked.pick, picked.format, sources.length)) return;

    if (priority !== 'hero') return;

    if (!isAlphaVideoVisible(video)) {
      video.dataset.alphaWaitVisible = 'true';
      return;
    }

    if (picked.format === 'mp4' || picked.format === 'hevc') {
      preloadMobileOnce(picked.pick.src);
    }
    startAlphaVideo(video);
  }

  global.SigmaAlphaVideo = {
    needMobileMp4FromUA: needMobileMp4FromUA,
    needHevcFromUA: needMobileMp4FromUA,
    pickAlphaSource: pickAlphaSource,
    isAlphaVideoVisible: isAlphaVideoVisible,
    applyPickedSource: applyPickedSource,
    preloadMobileOnce: preloadMobileOnce,
    preloadHevcOnce: preloadMobileOnce,
    tryPlay: tryPlay,
    startAlphaVideo: startAlphaVideo,
    initFromInline: initFromInline,
    applyIosInlineAttrs: applyIosInlineAttrs,
  };
})(typeof window !== 'undefined' ? window : this);
